import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Eye, ImageIcon, Loader2, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getMediaUrl } from '../api';
import { downloadCrossOrigin } from '../lib/download-blob';
import { fileTypeInfo } from '../lib/file-type';
import { mediaBoxSize, videoBoxSize } from '../lib/media-box';
import type { ChatMessage } from '../types';
import { AudioPlayer } from './AudioPlayer';
import { ImageLightbox } from './ImageLightbox';
import { PdfPreviewModal } from './PdfPreviewModal';

interface Props {
  message: ChatMessage;
}

/**
 * Renderiza mídia inline conforme o `type` da mensagem:
 * - image  → <img> com lazy load
 * - audio  → AudioPlayer com waveform (Story 4.11)
 * - video  → <video controls>
 * - document/sticker → ícone + botão download
 *
 * Quando media_status='pending' (worker-media ainda baixando), mostra skeleton.
 * Quando 'failed', placeholder discreto.
 */
export function MediaContent({ message }: Props) {
  // Card de mídia (download/arquivo) acompanha a bolha: a enviada (outbound) é
  // sempre mint clara → overlay escuro sutil; a recebida (inbound) vira slate no
  // modo escuro → usa os tokens n-100/n-200.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Vídeo só monta o <video> real após o 1º clique no play. Antes disso é
  // poster-as-<img> + botão play — evita o spinner de "loading" nativo do
  // <video controls> (que baixa metadata do mp4 só pra popular a duração no
  // scrubber). Paridade com WhatsApp Web e com o VideoThumb do mobile.
  const [videoPlaying, setVideoPlaying] = useState(false);
  /** Bytes da imagem já chegaram? */
  const [imgLoaded, setImgLoaded] = useState(false);
  /**
   * O giro só entra depois de 100ms de espera.
   *
   * Trocar de conversa remonta a bolha, então o estado nasce "carregando" de
   * novo — e sem o atraso a imagem JÁ baixada piscava o giro à toa. Imagem em
   * cache resolve em poucos milissegundos e nunca chega a mostrar nada;
   * imagem que vem da rede passa dos 100ms e mostra normal.
   *
   * É o sintoma tratado, não a causa: o certo seria perguntar ao navegador se
   * o arquivo já está em cache, mas com `loading="lazy"` ele ainda nem começou
   * a busca no momento da checagem (testado — `img.complete` retorna false).
   * A causa some abrindo mão do lazy nas imagens visíveis, que muda o
   * comportamento de carregamento e merece tarefa própria com medição de banda.
   */
  const [mostrarGiro, setMostrarGiro] = useState(false);
  useEffect(() => {
    if (imgLoaded) return undefined;
    const id = setTimeout(() => setMostrarGiro(true), 100);
    return () => clearTimeout(id);
  }, [imgLoaded]);
  // Audio NÃO dispara useQuery — AudioPlayer busca a signed URL on-demand
  // no primeiro click no play (lazy igual AtenderBem). Image/video/document
  // precisam da URL pra renderizar thumbnail/player imediato.
  /**
   * URL ESTÁVEL que já vem na própria mensagem (`GET /tickets/:id/messages`
   * monta com `buildMediaUrl` — token determinístico, versionado pela storage
   * key). É `/api/v1/proxy/...`, servida com `immutable` + ETag: o navegador
   * guarda e nunca mais rebaixa. Quando ela existe, NÃO precisamos pedir URL
   * nenhuma.
   *
   * O caminho antigo (`getMediaUrl`) devolve uma signed URL do R2 que muda a
   * CADA chamada (assinatura + timestamp no query string). Pro navegador,
   * URL diferente = arquivo diferente → rebaixava os ~118 KB da foto toda vez
   * que a conversa era reaberta, e de novo a cada `refetchInterval` de 9 min
   * com a conversa aberta. Medido no srv01: 2.888 pedidos de URL por hora.
   *
   * Fica como RESERVA pro que não traz a URL pronta: mensagem recém-chegada
   * pelo evento de realtime (o payload não carrega `media_url`) e mídia que
   * ainda não estava `stored` na hora da listagem.
   */
  const urlPronta = message.media_url ?? null;

  const urlQ = useQuery({
    queryKey: ['media-url', message.id],
    queryFn: () => getMediaUrl(message.id),
    staleTime: 9 * 60 * 1000,
    refetchInterval: 9 * 60 * 1000,
    refetchIntervalInBackground: false,
    enabled:
      !urlPronta &&
      Boolean(message.media_id) &&
      message.media_status === 'stored' &&
      message.type !== 'audio',
  });

  // Skeleton durante download. Áudio espelha o layout do AudioPlayer final
  // (botão + onda + timer) com placeholders cinza pulsando — evita flash
  // de layout quando o player real entra. Image/video usam aspect-ratio
  // calculada das dims (quando disponíveis) pra bolha nascer com tamanho
  // final, sem "expande". Sem dims → fallback 120×220.
  const renderSkeleton = (): JSX.Element => {
    if (message.type === 'audio') {
      return (
        <div className="flex w-[260px] items-center gap-2 rounded-md bg-white/40 p-2 dark:bg-n-100">
          <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-pill bg-n-200" />
          <div className="flex h-8 flex-1 items-end gap-[2px]">
            {[6, 10, 14, 8, 16, 12, 6, 14, 10, 8, 12, 16, 10, 6, 14, 8, 12].map((h, i) => (
              <div
                key={i}
                className="w-[2px] flex-1 animate-pulse rounded-sm bg-n-200"
                style={{ height: `${h}px`, animationDelay: `${i * 60}ms` }}
              />
            ))}
          </div>
          <div className="h-2.5 w-7 animate-pulse rounded-sm bg-n-200" />
        </div>
      );
    }
    // Image/video: usa mesma lógica de fit do render real pra reservar
    // a caixa final ANTES do urlQ resolver. Sem isso, bolha nascia 120×220
    // e expandia pro tamanho final → "nascimento esquisito".
    const skel = mediaBoxSize(message.media_width, message.media_height);
    if (skel.height != null) {
      return (
        <div
          className="animate-pulse rounded-md bg-n-200"
          style={{ width: `${skel.width}px`, height: `${skel.height}px` }}
        />
      );
    }
    return <div className="h-[120px] w-[220px] animate-pulse rounded-md bg-n-200" />;
  };

  if (message.media_status === 'pending') return renderSkeleton();
  if (message.media_status === 'failed') {
    return (
      <div className="flex items-center gap-2 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
        <AlertTriangle size={14} />
        Mídia indisponível
      </div>
    );
  }

  // Audio: render imediato com WaveformBars + duração do servidor.
  // Signed URL só é buscada no primeiro click no play (lazy 100%).
  if (message.type === 'audio') {
    return (
      <AudioPlayer
        messageId={message.id}
        waveform={message.media_waveform ?? null}
        durationMs={message.media_duration_ms ?? null}
        urlPronta={urlPronta}
        mimePronto={message.media_mime ?? null}
      />
    );
  }

  // Vídeo ANTES do play: poster-as-<img> + botão play. Tratado aqui (antes do
  // early return do urlQ) porque NÃO precisa da signed URL do source — só do
  // poster, que já vem no payload/refetch (message.media_thumbnail_url) ou no
  // urlQ (urlQ.data.thumbnail_url). Não montar o <video> evita o spinner preto
  // de "loading" que o <video controls> mostra por ~1s só pra ler a duração do
  // mp4. Padrão WhatsApp Web + paridade com VideoThumb do mobile.
  if (message.type === 'video' && !videoPlaying) {
    // Poster: prioridade pro do payload/refetch (instantâneo ao reabrir
    // conversa); fallback pro do urlQ (inbound em tempo real). Dims idem.
    const poster =
      message.media_thumbnail_url ??
      urlQ.data?.stable_thumbnail_url ??
      urlQ.data?.thumbnail_url ??
      null;
    const dimW = message.media_width ?? urlQ.data?.width ?? null;
    const dimH = message.media_height ?? urlQ.data?.height ?? null;
    // Sem poster E sem URL ainda → skeleton. Caso contrário, mostra a caixa
    // (com poster se houver, senão preta) + botão play. A caixa tem tamanho
    // EXPLÍCITO (a MESMA usada no player) → dar play NÃO muda o tamanho da bolha.
    if (!poster && !urlPronta && (urlQ.isLoading || !urlQ.data?.url)) return renderSkeleton();
    const box = videoBoxSize(dimW, dimH);
    return (
      <button
        type="button"
        onClick={() => setVideoPlaying(true)}
        className="relative block overflow-hidden rounded-md bg-black"
        style={{ width: box.width, height: box.height }}
        aria-label="Reproduzir vídeo"
      >
        {poster && <img src={poster} alt="" className="block h-full w-full object-cover" />}
        {/* Play overlay — círculo branco translúcido + ícone escuro. */}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-white/85 shadow-md transition-transform hover:scale-105">
            <Play size={24} strokeWidth={0} className="ml-0.5 fill-n-800" />
          </span>
        </span>
      </button>
    );
  }

  if (!urlPronta && (urlQ.isLoading || !urlQ.data)) return renderSkeleton();

  // Link fixo do pedido (mídia que chegou ao vivo, sem `media_url`) antes da
  // URL assinada: a assinada vence em 10 min e, guardada no cache salvo,
  // voltava quebrada ao reabrir a conversa depois de um F5.
  const url = urlPronta ?? urlQ.data?.stable_url ?? (urlQ.data?.url as string);
  const mime_type = urlQ.data?.mime_type ?? message.media_mime ?? null;
  const size_bytes = urlQ.data?.size_bytes ?? message.media_size_bytes ?? null;
  const type = message.type;

  if (type === 'sticker') {
    // Sticker tem renderização própria (WhatsApp Web-style): sem bolha (o
    // background da MessageBubble vira transparente quando msg.type==='sticker'),
    // sem card de filename/download, sem zoom no click, tamanho compacto fixo,
    // `object-contain` pra preservar transparência (WhatsApp envia .webp).
    return (
      <div style={{ width: '140px', height: '140px' }}>
        <img
          src={url}
          alt={message.body ?? 'Figurinha'}
          loading="lazy"
          className="block h-full w-full object-contain"
        />
      </div>
    );
  }

  if (type === 'image') {
    // Preview da imagem em cima + card com filename/download embaixo.
    // Click na preview ou no card abre o lightbox.
    // Filename: usa o nome original do arquivo (uploaded). Em inbound o WhatsApp
    // não envia nome — caímos no genérico. NÃO usar body/caption como filename:
    // a legenda já é renderizada como texto embaixo da bolha; duplicar como
    // nome de arquivo é ruído.
    const filename = message.media_original_name || 'imagem.jpg';
    // Reserva espaço calculando dimensões finais quando o backend mandou
    // width/height da imagem (worker whatsmeow extrai do protobuf). Fita a
    // imagem numa caixa MAX 280×260 preservando aspect ratio — mesmo comportamento
    // do `max-h-[260px] w-full` antigo, mas reservado ANTES do byte chegar.
    // Bolha nasce com altura final, sem "abre pequeno e cresce". Sem
    // dimensões → fallback ao `max-h-[260px] w-full` (cresce, comportamento legacy).
    // Caixa fixa por orientação (helper compartilhado com MessageBubble): portrait
    // 190px, landscape 300px, altura máx 430px. A imagem dentro é `object-cover`
    // — preserva aspect-ratio e CROPA o excedente quando muito alta (screenshots
    // longos). Estilo WhatsApp Web: bolha não estica a thread; click abre lightbox.
    const box = mediaBoxSize(message.media_width, message.media_height);
    const fitted: { width: number; height: number } | null =
      box.height != null ? { width: box.width, height: box.height } : null;
    return (
      <>
        <div
          className="flex flex-col gap-2"
          style={fitted ? { width: `${fitted.width}px` } : { width: '300px' }}
        >
          {/* Preview clicável — dimensões finais reservadas quando dims existem */}
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            style={
              fitted ? { width: `${fitted.width}px`, height: `${fitted.height}px` } : undefined
            }
            className="relative block cursor-zoom-in overflow-hidden rounded-md p-0"
            aria-label="Ampliar imagem"
          >
            {!imgLoaded && mostrarGiro && (
              <div
                className="absolute inset-0 flex items-center justify-center rounded-md bg-n-200"
                aria-hidden
              >
                <Loader2 size={20} className="animate-spin text-n-400" />
              </div>
            )}
            <img
              src={url}
              alt={message.body ?? 'Imagem'}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              // Erro também encerra o giro: sem isso a bolha giraria pra sempre
              // quando o arquivo falha.
              onError={() => setImgLoaded(true)}
              // Sem transicao de opacidade: o giro ja cobre a espera, e o
              // fade somado a ele virava uma sequencia longa (gira -> some ->
              // clareia -> aparece). A imagem some/aparece de uma vez.
              className={[
                fitted
                  ? 'block h-full w-full rounded-md object-cover'
                  : 'block max-h-[260px] w-full rounded-md object-cover',
                imgLoaded ? 'opacity-100' : 'opacity-0',
              ].join(' ')}
            />
          </button>
          {/* Card filename + download */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setLightboxOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setLightboxOpen(true);
              }
            }}
            className="flex cursor-pointer items-center gap-3 rounded-md bg-white/70 px-3 py-2 transition hover:bg-white/95 dark:bg-n-100 dark:hover:bg-n-200"
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-sm bg-n-100 text-n-500">
              <ImageIcon size={16} />
            </span>
            <span className="flex-1 truncate text-[13px] text-n-800">{filename}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void downloadCrossOrigin(url, filename).catch((err) =>
                  console.error('download failed', err),
                );
              }}
              aria-label="Baixar imagem"
              title="Baixar"
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-pill text-n-600 hover:bg-n-100 hover:text-n-900"
            >
              <Download size={16} />
            </button>
          </div>
        </div>
        {lightboxOpen && (
          <ImageLightbox
            src={url}
            alt={message.body ?? 'Imagem'}
            filename={filename}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    );
  }

  // Audio é tratado antes (early return) — URL é buscada lazy pelo player.

  if (type === 'video') {
    // Origem do poster (em ordem de prioridade):
    //  1. message.media_thumbnail_url — outbound otimista (dataURL client-side
    //     capturado no canvas, instantâneo) OU refetch HTTP (URL HMAC).
    //  2. urlQ.data.thumbnail_url — signed URL S3 da thumb, vem JUNTO com o
    //     source neste mesmo fetch. Cobre INBOUND em tempo real (Cloud API +
    //     QR) sem mexer nos workers — a thumb já está no R2.
    const poster =
      message.media_thumbnail_url ??
      urlQ.data?.stable_thumbnail_url ??
      urlQ.data?.thumbnail_url ??
      null;
    // Chega aqui só com videoPlaying === true (user clicou no play do poster).
    // Usa a MESMA caixa do poster → o tamanho não muda ao dar play. autoPlay
    // porque o clique já foi a intenção de ver.
    const dimW = message.media_width ?? urlQ.data?.width ?? null;
    const dimH = message.media_height ?? urlQ.data?.height ?? null;
    const box = videoBoxSize(dimW, dimH);
    return (
      <div
        className="overflow-hidden rounded-md bg-black"
        style={{ width: box.width, height: box.height }}
      >
        <video
          controls
          autoPlay
          preload="auto"
          className="block h-full w-full"
          {...(poster && { poster })}
        >
          <source src={url} {...(mime_type ? { type: mime_type } : {})} />
        </video>
      </div>
    );
  }

  // document / unknown
  // PDF tem 2 ações: visualizar (modal inline) + baixar. Outros tipos: só baixar.
  // Ícone e cor variam por tipo (PDF vermelho, Excel verde, Word azul...) pra
  // identificação visual rápida.
  const isPdf = (mime_type ?? '').toLowerCase().includes('pdf');
  const filenameGuess = message.media_original_name || (isPdf ? 'documento.pdf' : 'Arquivo');
  const {
    Icon: TypeIcon,
    color: typeColor,
    label: typeLabel,
  } = fileTypeInfo(mime_type, filenameGuess);
  const handleDownload = (): void => {
    void downloadCrossOrigin(url, filenameGuess).catch((err) =>
      console.error('download failed', err),
    );
  };
  // Click no balão inteiro: PDF abre preview, demais baixam.
  const handleCardClick = (): void => {
    if (isPdf) setPdfOpen(true);
    else handleDownload();
  };
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className="flex w-[280px] max-w-full cursor-pointer items-center gap-3 rounded-md bg-white/40 px-3 py-2 text-[13px] text-n-800 transition hover:bg-white/70 dark:bg-n-100 dark:hover:bg-n-200"
      >
        <TypeIcon size={20} style={{ color: typeColor }} aria-label={typeLabel} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* Nome com extensão SEMPRE visível: base trunca, ext fica fixa no
              fim. Atendente sabe o tipo antes de clicar mesmo com nome longo. */}
          <span className="flex min-w-0 items-baseline font-medium" title={filenameGuess}>
            <span className="truncate">{splitExt(filenameGuess).base}</span>
            <span className="flex-shrink-0">{splitExt(filenameGuess).ext}</span>
          </span>
          {size_bytes && <span className="text-[11px] text-n-500">{formatBytes(size_bytes)}</span>}
        </div>
        {isPdf && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            aria-label="Baixar"
            title="Baixar"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-0 hover:text-brand-600"
          >
            <Download size={16} />
          </button>
        )}
        {!isPdf && <Download size={16} className="flex-shrink-0 text-n-500" />}
      </div>
      {isPdf && (
        <PdfPreviewModal
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          url={url}
          filename={filenameGuess}
        />
      )}
    </>
  );
}

/**
 * Separa nome em base + extensão preservada (ex: "Relatório Q1 2026.pdf" →
 * { base: "Relatório Q1 2026", ext: ".pdf" }). Usado pra renderizar o nome
 * truncado mas com a extensão SEMPRE visível no fim.
 */
function splitExt(name: string): { base: string; ext: string } {
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return { base: name, ext: '' };
  return { base: name.slice(0, idx), ext: name.slice(idx) };
}

/**
 * Caixa de exibição do vídeo em PIXELS explícitos — usada IGUAL no poster e no
 * player, pra dar play NÃO mudar o tamanho da bolha (paisagem cap 300px largura,
 * retrato cap 240px, altura sempre cap 320px; nunca amplia). Sem dims → 16:9
 * 300×169 (o worker extrai as dims na ingestão; NULL é raro/legado).
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
